import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, X } from 'lucide-react';
import {
  getClosedDealAttachmentsByDealId,
  getClosedDealKycStatus
} from '../../services/api/implementation/impl';
import {
  evaluateKycVerification,
  kycVerifyBadgeClasses
} from './postSalesKyc';
import DealKycDocumentsSection from './DealKycDocumentsSection';

/**
 * Read-only KYC panel for Accounts — docs + verified status for a closed deal.
 */
const AccountsKycPanel = ({ dealId, dealLabel, onClose }) => {
  const { data: attachmentResult, isLoading: loadingAttachments } = useQuery({
    queryKey: ['closedDealAttachments', dealId, 'accounts'],
    queryFn: () => getClosedDealAttachmentsByDealId(dealId),
    enabled: Boolean(dealId)
  });

  const { data: kycStatus, isLoading: loadingStatus } = useQuery({
    queryKey: ['closedDealKycStatus', dealId, 'accounts-panel'],
    queryFn: () => getClosedDealKycStatus(dealId),
    enabled: Boolean(dealId)
  });

  const verification = evaluateKycVerification(kycStatus);
  const attachments = attachmentResult?.attachments ?? [];
  const loading = loadingAttachments || loadingStatus;

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-6">
      <div
        className="absolute inset-0"
        onClick={onClose}
        aria-hidden
      />
      <div className="relative z-[81] flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl sm:rounded-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-4 py-3 sm:px-6">
          <div>
            <h2 className="text-base font-semibold text-slate-900">KYC verification</h2>
            <p className="mt-0.5 text-xs text-slate-500">
              {dealLabel || `Deal #${dealId}`} — read only
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${kycVerifyBadgeClasses(
                verification.status
              )}`}>
              {verification.label}
            </span>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"
              aria-label="Close">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="overflow-y-auto p-4 sm:p-6">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading KYC…
            </div>
          ) : (
            <DealKycDocumentsSection
              dealId={dealId}
              attachments={attachments}
              readOnly
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountsKycPanel;
